import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Select, Space, message, Typography, Row, Col, DatePicker, Radio, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../../services/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CollectionForm = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const location = useLocation();

    useEffect(() => {
        if (isEdit) {
            fetchTaskDetail();
        } else if (location.state?.template) {
            // 如果是通过点击系统预设模板进来的
            const t = location.state.template;
            form.setFieldsValue({
                title: t.title,
                description: t.description,
                target: 'wechat_parent', // 默认推荐使用家长微信小程序
                fields: t.fields
            });
        } else {
            // 默认提供一个字段
            form.setFieldsValue({
                target: 'wechat_parent',
                fields: [{ id: 'field_' + Date.now(), label: '选项1', type: 'text', required: true }]
            });
        }
    }, [id, location.state, form]);

    const fetchTaskDetail = async () => {
        setFetching(true);
        try {
            const response = await api.get(`/data-collection/tasks/${id}`);
            if (response.data.success) {
                const task = response.data.data;
                // 处理日期格式
                if (task.deadline) {
                    task.deadline = moment(task.deadline);
                }
                form.setFieldsValue(task);
            } else {
                message.error('获取任务详情失败: ' + response.data.message);
                navigate('/data-collection');
            }
        } catch (error) {
            console.error('获取任务详情出错:', error);
            message.error('获取任务详情出错');
        } finally {
            setFetching(false);
        }
    };

    const onFinish = async (values) => {
        // 确保有字段
        if (!values.fields || values.fields.length === 0) {
            message.error('表单至少需要包含一个字段！');
            return;
        }

        // 格式化日期
        const payload = {
            ...values,
            deadline: values.deadline ? values.deadline.format('YYYY-MM-DD HH:mm:ss') : null
        };

        setLoading(true);
        try {
            let response;
            if (isEdit) {
                response = await api.put(`/data-collection/tasks/${id}`, payload);
            } else {
                response = await api.post('/data-collection/tasks', payload);
            }

            if (response.data.success) {
                message.success(isEdit ? '更新成功' : '创建成功');
                navigate('/data-collection');
            } else {
                message.error((isEdit ? '更新失败: ' : '创建失败: ') + response.data.message);
            }
        } catch (error) {
            console.error('保存任务出错:', error);
            message.error('保存出错，请检查必填项是否完整');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            bordered={false}
            loading={fetching}
            title={
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/data-collection')} type="text" />
                    <Title level={4} style={{ margin: 0 }}>{isEdit ? '编辑收集任务' : '新建收集任务'}</Title>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ target: 'desktop', status: 'active' }}
                style={{ maxWidth: 800, margin: '0 auto' }}
            >
                <Card title="基本信息" size="small" style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="title"
                                label="表单标题"
                                rules={[{ required: true, message: '请输入表单标题' }]}
                            >
                                <Input placeholder="例如：周末家长问卷、课后满意度调查" size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="description"
                                label="描述说明 (可选)"
                            >
                                <TextArea rows={3} placeholder="表单的详细说明..." />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="target"
                                label="收集目标设备"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="desktop">教室桌面端 (学生)</Option>
                                    <Option value="wechat_parent">微信小程序 (家长)</Option>
                                    <Option value="all">两端均可</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="status"
                                label="初始状态"
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="draft">草稿</Option>
                                    <Option value="active">使用中 (开放收集)</Option>
                                    <Option value="finished">已结束</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="deadline"
                                label="截止日期 (可选)"
                            >
                                <DatePicker showTime style={{ width: '100%' }} placeholder="选择截止时间" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card title="表单字段设计" size="small" style={{ marginBottom: 24 }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        自定义您想要收集的信息。系统会自动记录填写人的身份信息（如学生姓名），无需重复添加。
                    </Text>

                    <Form.List name="fields">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <Card
                                        key={key}
                                        type="inner"
                                        title={`字段 ${index + 1}`}
                                        extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: 'red' }} />}
                                        style={{ marginBottom: 16 }}
                                    >
                                        {/* 隐藏的ID字段 */}
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'id']}
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>

                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'label']}
                                                    label="字段标题 (问题)"
                                                    rules={[{ required: true, message: '请输入问题内容' }]}
                                                >
                                                    <Input placeholder="例如：您的意见是？" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'type']}
                                                    label="类型"
                                                    rules={[{ required: true }]}
                                                >
                                                    <Select>
                                                        <Option value="text">单行文本</Option>
                                                        <Option value="number">数字</Option>
                                                        <Option value="radio">单项选择</Option>
                                                        <Option value="checkbox">多项选择</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'required']}
                                                    label="是否必填"
                                                    valuePropName="checked"
                                                    initialValue={true}
                                                >
                                                    <Radio.Group>
                                                        <Radio value={true}>是</Radio>
                                                        <Radio value={false}>否</Radio>
                                                    </Radio.Group>
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prevValues, currentValues) => {
                                                const prevFields = prevValues.fields || [];
                                                const currFields = currentValues.fields || [];
                                                return prevFields[name]?.type !== currFields[name]?.type;
                                            }}
                                        >
                                            {({ getFieldValue }) => {
                                                const type = getFieldValue(['fields', name, 'type']);
                                                if (type === 'radio' || type === 'checkbox') {
                                                    return (
                                                        <Form.Item label="选项设置" required style={{ marginBottom: 0 }}>
                                                            <Form.List
                                                                name={[name, 'options']}
                                                                initialValue={['选项1', '选项2']}
                                                            >
                                                                {(optFields, { add: addOpt, remove: removeOpt }, { errors }) => (
                                                                    <>
                                                                        {optFields.map((optField) => (
                                                                            <Form.Item
                                                                                {...optField}
                                                                                key={optField.key}
                                                                                validateTrigger={['onChange', 'onBlur']}
                                                                                rules={[{ required: true, whitespace: true, message: "请输入选项内容或删除此项" }]}
                                                                                style={{ marginBottom: 8 }}
                                                                            >
                                                                                <Input
                                                                                    placeholder="请输入选项内容"
                                                                                    style={{ width: '80%', marginRight: 8 }}
                                                                                    addonAfter={
                                                                                        optFields.length > 1 ? (
                                                                                            <MinusCircleOutlined onClick={() => removeOpt(optField.name)} style={{ color: 'red', cursor: 'pointer' }} />
                                                                                        ) : null
                                                                                    }
                                                                                />
                                                                            </Form.Item>
                                                                        ))}
                                                                        <Form.Item>
                                                                            <Button
                                                                                type="dashed"
                                                                                onClick={() => addOpt()}
                                                                                style={{ width: '80%' }}
                                                                                icon={<PlusOutlined />}
                                                                            >
                                                                                添加选项
                                                                            </Button>
                                                                            <Form.ErrorList errors={errors} />
                                                                        </Form.Item>
                                                                    </>
                                                                )}
                                                            </Form.List>
                                                        </Form.Item>
                                                    );
                                                }
                                                return null;
                                            }}
                                        </Form.Item>
                                    </Card>
                                ))}

                                <Button
                                    type="dashed"
                                    onClick={() => add({ id: 'field_' + Date.now(), type: 'text', required: true })}
                                    block
                                    icon={<PlusOutlined />}
                                    size="large"
                                >
                                    添加一个新字段
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                <Form.Item style={{ textAlign: 'center', marginTop: 32 }}>
                    <Space size="large">
                        <Button onClick={() => navigate('/data-collection')} size="large">
                            取消
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading} size="large">
                            {isEdit ? '保存更改' : '创建收集任务'}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default CollectionForm;
